import { useAddOrganizationMutation, useOrganizationListQuery } from 'app/modules/organizations/api/organizationsApiHooks';
import { FormikContext, useField } from 'formik';
import { useContext, useEffect, useMemo, useState } from 'react';
import { CreatableSelect, FormGroup, Select, Text, extractStylingProps, useToasts } from 'tombac';
import "./OrganizationPicker.css";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const defaultOptionFilter = () => true;
function OrganizationPicker({
  allowCreate = true,
  caption,
  disabled = false,
  id = 'organization-select',
  label = 'Organization',
  onOrganizationDetails,
  optionFilter = defaultOptionFilter,
  required = false,
  ...rest
}) {
  const [field, meta, helpers] = 'name' in rest && useContext(FormikContext) // If within Formik's context use it instead of props
  ? useField(rest.name) : [];
  const value = 'name' in rest ? field.value : rest.value;
  const onChange = 'name' in rest ? helpers.setValue : rest.onChange;
  const {
    addToast
  } = useToasts();
  const {
    error: loadError,
    data: orgs,
    isLoading
  } = useOrganizationListQuery();
  useEffect(() => {
    if (loadError) {
      addToast('Failed to load organization list', 'danger');
      console.error(loadError);
    }
  }, [loadError]);
  const {
    data: addedOrg,
    error: addErrorResponse,
    isLoading: isAdding,
    mutate: addOrganization
  } = useAddOrganizationMutation();
  const [addError, setAddError] = useState();
  useEffect(() => {
    if (addErrorResponse) {
      addErrorResponse.json().then(value => setAddError(value.message ?? value));
    } else {
      setAddError(undefined);
    }
  }, [addErrorResponse]);
  useEffect(() => {
    if (addedOrg) {
      onChange && onChange(addedOrg.id);
      onOrganizationDetails && onOrganizationDetails(orgs.find(o => o.id === addedOrg.id));
    }
  }, [addedOrg, onOrganizationDetails && orgs]);
  useEffect(() => {
    if (onOrganizationDetails && orgs && value) {
      onOrganizationDetails(orgs.find(o => o.id === value));
    }
  }, [orgs, value]);
  const options = useMemo(() => orgs && orgs.filter(optionFilter).map(org => ({
    label: org.name,
    value: org.id
  })), [optionFilter, orgs]);
  const selectValue = useMemo(() => value !== undefined && value !== null && options ? options.find(option => option.value === value) : undefined, [options, value]);
  const handleChange = option => {
    onChange && onChange(option?.value);
    onOrganizationDetails && onOrganizationDetails(orgs.find(o => o.id === option?.value));
  };
  const handleCreate = async inputValue => {
    addOrganization({
      organization: {
        name: inputValue
      }
    });
  };
  const OrganizationSelect = allowCreate ? CreatableSelect : Select;
  const isError = Boolean(addError ?? (meta?.touched && meta.error));
  const creatableProps = allowCreate ? {
    onCreateOption: handleCreate
  } : undefined;
  const [stylingProps] = extractStylingProps(rest);
  return /*#__PURE__*/_jsx(FormGroup, {
    $w: "100%",
    ...stylingProps,
    caption: /*#__PURE__*/_jsxs(_Fragment, {
      children: [isError && /*#__PURE__*/_jsx(Text, {
        size: "s",
        variant: "danger",
        children: addError ?? meta?.error
      }), caption !== undefined ? caption : allowCreate ? /*#__PURE__*/_jsx(Text, {
        size: "s",
        variant: "secondary",
        children: "Start typing to find or add organization."
      }) : undefined]
    }),
    danger: isError,
    htmlFor: id,
    label: label,
    required: required,
    variant: isError ? 'danger' : 'default',
    "data-sentry-element": "FormGroup",
    "data-sentry-component": "OrganizationPicker",
    "data-sentry-source-file": "OrganizationPicker.tsx",
    children: /*#__PURE__*/_jsx(OrganizationSelect, {
      $w: "100%",
      className: "OrganizationsPicker__select",
      id: id,
      invalid: isError,
      isDisabled: disabled || !options || isLoading || isAdding,
      isLoading: !options || isLoading || isAdding,
      isSearchable: true,
      onBlur: field?.onBlur,
      onChange: handleChange,
      options: options,
      value: selectValue,
      ...creatableProps,
      "data-sentry-element": "OrganizationSelect",
      "data-sentry-source-file": "OrganizationPicker.tsx",
      "data-sentry-element": "OrganizationSelect"
    })
  });
}
export default OrganizationPicker;